(() => {
  if (window.VybeyBundlePricing) return;
  const cache = new Map();
  const queue = new Map();
  let timer;
  const warmed = new Map();
  const notify = () => window.dispatchEvent(new CustomEvent('vybey:bundle-pricing'));
  const config = () => window.bundlePricingConfig || {};
  const currency = () => window.Shopify?.currency?.active || config().currency;
  const gid = (kind, id) => String(id).startsWith('gid://') ? String(id) : `gid://shopify/${kind}/${id}`;
  const normalize = (lines) => {
    const merged = new Map();
    for (const line of lines) {
      const quantity = Number(line.quantity);
      if (!Number.isInteger(quantity) || quantity < 1 || !line.merchandiseId) throw new Error('Invalid quote line');
      const id = gid('ProductVariant', line.merchandiseId);
      merged.set(id, (merged.get(id) || 0) + quantity);
    }
    return [...merged].sort(([a], [b]) => a.localeCompare(b)).map(([merchandiseId, quantity]) => ({merchandiseId, quantity}));
  };
  const selectionLines = (fallbackVariant, fallbackQuantity = 1) => {
    const rows = [...document.querySelectorAll('.bundle-flavour-item')];
    const selected = rows.filter(row => Number(row.querySelector('.qty-input')?.value) > 0);
    if (selected.length) return normalize(selected.map(row => ({
      merchandiseId: row.dataset.variantId,
      quantity: Number(row.querySelector('.qty-input').value)
    })));
    const variant = fallbackVariant || (window.bundleVariants || []).find(v => v.available);
    return variant ? normalize([{merchandiseId: variant.id, quantity: rows.length ? 1 : Math.max(1, Number(fallbackQuantity) || 1)}]) : [];
  };
  const makeRequest = (lines, sellingPlanId) => {
    const plan = sellingPlanId ? gid('SellingPlan', sellingPlanId) : null;
    const context = {country: config().country, currency: currency()};
    if (!context.country || !context.currency || !lines.length) throw new Error('Quote context unavailable');
    const normalized = normalize(lines);
    return {
      key: JSON.stringify([context.country, context.currency, plan, normalized]),
      expectedCurrency: context.currency,
      input: {
        buyerIdentity: {countryCode: context.country},
        lines: normalized.map(line => ({...line, ...(plan ? {sellingPlanId: plan} : {})}))
      }
    };
  };
  const flush = async () => {
    timer = null;
    const batch = [...queue.values()].slice(0, 8);
    batch.forEach(entry => queue.delete(entry.key));
    if (queue.size) timer = setTimeout(flush, 0);
    const definitions = batch.map((_, i) => `$input${i}: CartInput!`).join(', ');
    const fields = batch.map((_, i) => `q${i}:cartCreate(input:$input${i}){cart{cost{totalAmount{amount currencyCode}}}userErrors{message}warnings{code message}}`).join(' ');
    const variables = Object.fromEntries(batch.map((entry, i) => [`input${i}`, entry.input]));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST', credentials: 'omit', signal: controller.signal,
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({query: `mutation BundlePriceQuote(${definitions}){${fields}}`, variables})
      });
      if (!response.ok) throw new Error('Quote request failed');
      const payload = await response.json();
      if (payload.errors?.length) throw new Error('Quote query rejected');
      batch.forEach((entry, i) => {
        const result = payload.data?.[`q${i}`];
        const total = result?.cart?.cost?.totalAmount;
        const amount = Number(total?.amount);
        if (!result || result.userErrors?.length || result.warnings?.length || !Number.isFinite(amount) || amount < 0 || total.currencyCode !== entry.expectedCurrency) {
          entry.status = 'error';
          entry.expires = Date.now() + 3000;
        } else {
          entry.status = 'ready';
          entry.cents = Math.round(amount * 100);
          entry.currency = total.currencyCode;
          entry.expires = Date.now() + 30000;
        }
      });
    } catch (_) {
      batch.forEach(entry => { entry.status = 'error'; entry.expires = Date.now() + 3000; });
    } finally {
      clearTimeout(timeout);
      notify();
    }
  };
  const read = (lines, sellingPlanId) => {
    let request;
    try { request = makeRequest(lines, sellingPlanId); } catch (_) { return {status: 'error'}; }
    const old = cache.get(request.key);
    if (old && (old.status === 'pending' || old.expires > Date.now())) return old;
    const entry = {...request, status: 'pending'};
    cache.set(request.key, entry);
    queue.set(request.key, entry);
    if (cache.size > 100) {
      for (const [key, value] of cache) {
        if (value.status !== 'pending') { cache.delete(key); break; }
      }
    }
    if (!timer) timer = setTimeout(flush, 0);
    return entry;
  };
  const format = (cents, code = currency()) => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style: 'currency', currency: code}).format(cents / 100);
  const text = (entry) => entry.status === 'ready' ? format(entry.cents, entry.currency) : entry.status === 'error' ? config().labels?.unavailable || '' : '';
  const prefetch = (lines, sellingPlanId) => {
    let normalized, key;
    try { normalized = normalize(lines); key = makeRequest(normalized, sellingPlanId).key; } catch (_) { return; }
    if (warmed.get(key) > Date.now()) return;
    const current = read(normalized, sellingPlanId);
    if (current.status !== 'ready') return;
    warmed.set(key, Date.now() + 30000);
    // Warm exact selections only. Never reuse another flavour's discount quote.
    if (normalized.length === 1) {
      const id = normalized[0].merchandiseId;
      for (let quantity = 1; quantity <= 6; quantity++) read([{merchandiseId: id, quantity}], sellingPlanId);
      if (normalized[0].quantity >= 6) read([{merchandiseId: id, quantity: normalized[0].quantity + 1}], sellingPlanId);
    } else {
      // For mixed bundles, preload adding/removing one of the selected flavours.
      normalized.forEach((line, index) => {
        for (const delta of [-1, 1]) {
          const next = normalized.map((item, i) => ({...item, quantity: item.quantity + (i === index ? delta : 0)})).filter(item => item.quantity > 0);
          if (next.length) read(next, sellingPlanId);
        }
      });
    }
    if (warmed.size > 100) warmed.delete(warmed.keys().next().value);
  };
  const invalidate = () => { for (const [key, value] of cache) if (value.status !== 'pending') cache.delete(key); notify(); };
  window.VybeyBundlePricing = {read, selectionLines, format, text, invalidate, prefetch};
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') invalidate(); });
})();
